/**
 * Progress Indicator Component
 *
 * Shows progress of issues addressed (accepted + ignored) with
 * a progress bar and celebration state when all issues are reviewed.
 *
 * Part of Score Chase (Track A) gamification pattern.
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '@/lib/analytics/events';
import type { ProgressIndicatorProps } from './types';
import { styles } from './inlineAnnotationsStyles';

/**
 * ProgressIndicator - Shows addressed/total issues with celebration
 */
export function ProgressIndicator({
  addressedCount,
  totalCount,
  testID,
}: ProgressIndicatorProps) {
  const { t } = useTranslation('features');
  const prevAddressedRef = useRef(addressedCount);
  const hasTrackedCompletionRef = useRef(false);

  const progress = totalCount > 0 ? (addressedCount / totalCount) * 100 : 0;
  const isComplete = addressedCount >= totalCount && totalCount > 0;

  // Track progress updates
  useEffect(() => {
    if (totalCount === 0) return;
    if (addressedCount > prevAddressedRef.current) {
      trackEvent('Score_Chase_Progress_Updated', {
        addressed_count: addressedCount,
        total_count: totalCount,
        progress_percent: Math.round(progress),
      });
    }
    prevAddressedRef.current = addressedCount;
  }, [addressedCount, totalCount]);

  // Track completion (all issues reviewed) - fire once
  useEffect(() => {
    if (isComplete && !hasTrackedCompletionRef.current) {
      hasTrackedCompletionRef.current = true;
      trackEvent('Score_Chase_All_Reviewed', {
        total_count: totalCount,
      });
    }
  }, [isComplete, totalCount]);

  // Don't render if no issues
  if (totalCount === 0) {
    return null;
  }

  return (
    <View
      style={styles.progressContainer}
      testID={testID}
      accessibilityRole="progressbar"
      accessibilityLabel={
        isComplete
          ? t('inline_annotations.progress_complete_aria', { total: totalCount })
          : undefined
      }
      accessibilityValue={{
        min: 0,
        max: totalCount,
        now: addressedCount,
      }}
    >
      <View style={styles.progressBar}>
        <View
          style={[
            styles.progressFill,
            { width: `${progress}%` },
            isComplete && progressStyles.completeFill,
          ]}
        />
      </View>
      {isComplete ? (
        <Text style={[styles.progressText, progressStyles.completeText]} testID={`${testID}-complete`}>
          {t('inline_annotations.all_reviewed', {
            defaultValue: 'All issues reviewed!',
          })}
        </Text>
      ) : (
        <Text style={styles.progressText}>
          {t('inline_annotations.progress', {
            addressed: addressedCount,
            total: totalCount,
          })}
        </Text>
      )}
    </View>
  );
}

const progressStyles = StyleSheet.create({
  completeFill: {
    backgroundColor: '#059669',
  },
  completeText: {
    color: '#059669',
    fontWeight: '700',
  },
});
